import React, { useEffect } from 'react';
import {
    TextInput,
    NumberInput,
    Switch,
    Button,
    Textarea,
    TagsInput,
    Text,
    Loader,
    Alert,
    Stack,
    MultiSelect,
    Input,
} from '@mantine/core';
import { DateTimePicker, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconSettings } from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { GET_APP_CONFIG } from '../../apollo/queries/appConfig';
import { DEFAULT_APP_VAT_PERCENTAGE } from '../../constants/appConfigDefaults';
import { UPDATE_APP_CONFIG } from '../../apollo/mutations/appConfig';
import { GET_DIVISIONS } from '../../apollo/queries/division';
import { isSalesDataMaintenanceActive } from '../../hooks/useSalesDataMaintenance';
import { SectionCard } from './SectionCard';
import { ReasonListEditor } from './ReasonListEditor';
import { CountryVatConfigManager } from './CountryVatConfigManager';
import { TaxCodeConfigManager } from './TaxCodeConfigManager';
import { MaintenanceTabPanelHeader } from '../common/MaintenanceTabPanelHeader';
import type { UpdateAppConfigInput } from '../../codegen/graphql';

dayjs.extend(utc);
dayjs.extend(timezone);

type AppConfigFormValues = {
    maintenanceMode: boolean;
    maintenanceMessage: string;
    surveyAutoGenEnabled: boolean;
    surveyAutoGenDueDays: number;
    vatPercentage: number;
    divisionManagerApprovalSlaMinutes: number;
    opsExecApprovalSlaMinutes: number;
    seniorOpsExecApprovalSlaMinutes: number;
    procurementManagerApprovalSlaMinutes: number;
    procurementExecApprovalSlaMinutes: number;
    procurementSupplierEngagementSlaMinutes: number;
    androidAppVersion: string;
    iosAppVersion: string;
    huaweiAppVersion: string;
    appUpdateRequired: boolean;
    appUpdateMessage: string;
    excludedCountryCodes: string;
    excludedDivisionSapIds: string;
    dailyIncompleteSalesReportRecipients: string;
    dmOpsDeclineReasons: string;
    procurementDeclineReasons: string;
    supplierDeclineReasons: string;
    interimPriceReasons: string;
    salesReviewEnabled: boolean;
    manualSalesAutoDataMaxAgeHours: number;
    pendingPriceChangeRequestsDigestHours: string;
    divisionalManagerPendingPriceChangeRequestsDigestHours: string;
    divisionalManagerPriceChangeRequestOutcomesDigestHours: string;
    storeManagerPriceChangeRequestOutcomesDigestHours: string;
    pricingBatchesModuleEnabled: boolean;
    priceChangeFreezeStartAt: Date | null;
    priceChangeFreezeEndAt: Date | null;
    priceChangeFreezeBannerMessage: string;
    salesDataMaintenanceStartTime: string;
    salesDataMaintenanceEndTime: string;
    letsCreatePriceSyncEnabled: boolean;
    trainingModuleEnabled: boolean;
};

const DEFAULT_PENDING_PRICE_CHANGE_REQUEST_DIGEST_HOURS = '7,10,13,16';
const DEFAULT_DM_SM_DIGEST_HOURS = '7,8,9,10,11,12,13,14,15,16,17,18';

const initialValues: AppConfigFormValues = {
    maintenanceMode: false,
    maintenanceMessage: '',
    surveyAutoGenEnabled: true,
    surveyAutoGenDueDays: 14,
    vatPercentage: DEFAULT_APP_VAT_PERCENTAGE,
    divisionManagerApprovalSlaMinutes: 2880,
    opsExecApprovalSlaMinutes: 2880,
    seniorOpsExecApprovalSlaMinutes: 2880,
    procurementManagerApprovalSlaMinutes: 4320,
    procurementExecApprovalSlaMinutes: 4320,
    procurementSupplierEngagementSlaMinutes: 4320,
    androidAppVersion: '1.4.2',
    iosAppVersion: '1.4.2',
    huaweiAppVersion: '1.4.2',
    appUpdateRequired: false,
    appUpdateMessage:
        'To continue using the app, you need to install the latest version.',
    excludedCountryCodes: 'MW',
    excludedDivisionSapIds: '',
    dailyIncompleteSalesReportRecipients: '',
    dmOpsDeclineReasons: '',
    procurementDeclineReasons: '',
    supplierDeclineReasons: '',
    interimPriceReasons: '',
    salesReviewEnabled: false,
    manualSalesAutoDataMaxAgeHours: 2,
    pendingPriceChangeRequestsDigestHours:
        DEFAULT_PENDING_PRICE_CHANGE_REQUEST_DIGEST_HOURS,
    divisionalManagerPendingPriceChangeRequestsDigestHours:
        DEFAULT_DM_SM_DIGEST_HOURS,
    divisionalManagerPriceChangeRequestOutcomesDigestHours:
        DEFAULT_DM_SM_DIGEST_HOURS,
    storeManagerPriceChangeRequestOutcomesDigestHours:
        DEFAULT_DM_SM_DIGEST_HOURS,
    pricingBatchesModuleEnabled: false,
    priceChangeFreezeStartAt: null,
    priceChangeFreezeEndAt: null,
    priceChangeFreezeBannerMessage: '',
    salesDataMaintenanceStartTime: '',
    salesDataMaintenanceEndTime: '',
    letsCreatePriceSyncEnabled: false,
    trainingModuleEnabled: false,
};

function commaSeparatedToArray(str: string): string[] {
    return str
        ? str
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
        : [];
}

function arrayToCommaSeparated(arr: string[]): string {
    return arr.join(',');
}

const SOUTH_AFRICA_TIMEZONE = 'Africa/Johannesburg';
const MAINTENANCE_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

function salesMaintenanceTimeError(start: string, end: string): string | null {
    const startTime = start.trim().slice(0, 5);
    const endTime = end.trim().slice(0, 5);
    if (!startTime && !endTime) {
        return null;
    }
    if (!startTime || !endTime) {
        return 'Set both times, or clear both.';
    }
    if (
        !MAINTENANCE_TIME_PATTERN.test(startTime) ||
        !MAINTENANCE_TIME_PATTERN.test(endTime)
    ) {
        return 'Use HH:mm (Africa/Johannesburg).';
    }
    if (startTime === endTime) {
        return 'Start and end must be different.';
    }
    return null;
}

function normalisePriceChangeFreezeEndAtForPicker(
    value: Date | string | null,
): Date | null {
    if (!value) {
        return null;
    }
    const local = dayjs(value);
    const calendarDate = local.format('YYYY-MM-DD');
    const time = local.format('HH:mm:ss');
    return dayjs.tz(`${calendarDate} ${time}`, SOUTH_AFRICA_TIMEZONE).toDate();
}

function getPriceChangeFreezeEndAtPickerValue(value: Date | null): Date | null {
    if (!value) {
        return null;
    }
    const jhb = dayjs(value).tz(SOUTH_AFRICA_TIMEZONE);
    const calendarDate = jhb.format('YYYY-MM-DD');
    const time = jhb.format('HH:mm:ss');
    return dayjs(`${calendarDate} ${time}`).toDate();
}

function getPriceChangeFreezeMinStartDate(): Date {
    return dayjs().tz(SOUTH_AFRICA_TIMEZONE).startOf('day').toDate();
}

function getPriceChangeFreezeMinEndDate(startAt: Date | null): Date {
    const baseDay = startAt
        ? dayjs(startAt).tz(SOUTH_AFRICA_TIMEZONE).startOf('day')
        : dayjs().tz(SOUTH_AFRICA_TIMEZONE).startOf('day');
    return baseDay.add(1, 'day').toDate();
}

function isPriceChangeFreezeEndAfterStart(startAt: Date, endAt: Date): boolean {
    const startDay = dayjs(startAt).tz(SOUTH_AFRICA_TIMEZONE).startOf('day');
    const endDay = dayjs(endAt).tz(SOUTH_AFRICA_TIMEZONE).startOf('day');
    return endDay.isAfter(startDay);
}

function normalisePendingPriceChangeDigestHours(values: string[]): string {
    const normalised = values
        .map(value => value.trim())
        .filter(value => {
            if (!/^\d+$/.test(value)) {
                return false;
            }
            const hour = Number.parseInt(value, 10);
            return hour >= 0 && hour <= 23;
        })
        .map(value => String(Number.parseInt(value, 10)));
    const uniqueSorted = [...new Set(normalised)].sort(
        (a, b) => Number(a) - Number(b),
    );
    return arrayToCommaSeparated(uniqueSorted);
}

function buildUpdateAppConfigInput(
    values: AppConfigFormValues,
): UpdateAppConfigInput {
    return {
        maintenanceMode: values.maintenanceMode,
        maintenanceMessage: values.maintenanceMessage || null,
        surveyAutoGenEnabled: values.surveyAutoGenEnabled,
        surveyAutoGenDueDays: values.surveyAutoGenDueDays,
        vatPercentage: values.vatPercentage,
        divisionManagerApprovalSlaMinutes:
            values.divisionManagerApprovalSlaMinutes,
        opsExecApprovalSlaMinutes: values.opsExecApprovalSlaMinutes,
        seniorOpsExecApprovalSlaMinutes: values.seniorOpsExecApprovalSlaMinutes,
        procurementManagerApprovalSlaMinutes:
            values.procurementManagerApprovalSlaMinutes,
        procurementExecApprovalSlaMinutes:
            values.procurementExecApprovalSlaMinutes,
        procurementSupplierEngagementSlaMinutes:
            values.procurementSupplierEngagementSlaMinutes,
        androidAppVersion: values.androidAppVersion,
        iosAppVersion: values.iosAppVersion,
        huaweiAppVersion: values.huaweiAppVersion,
        appUpdateRequired: values.appUpdateRequired,
        appUpdateMessage: values.appUpdateMessage,
        excludedCountryCodes: values.excludedCountryCodes,
        excludedDivisionSapIds: values.excludedDivisionSapIds,
        dailyIncompleteSalesReportRecipients:
            values.dailyIncompleteSalesReportRecipients,
        dmOpsDeclineReasons: values.dmOpsDeclineReasons,
        procurementDeclineReasons: values.procurementDeclineReasons,
        supplierDeclineReasons: values.supplierDeclineReasons,
        interimPriceReasons: values.interimPriceReasons,
        salesReviewEnabled: values.salesReviewEnabled,
        manualSalesAutoDataMaxAgeHours: values.manualSalesAutoDataMaxAgeHours,
        pendingPriceChangeRequestsDigestHours:
            values.pendingPriceChangeRequestsDigestHours,
        divisionalManagerPendingPriceChangeRequestsDigestHours:
            values.divisionalManagerPendingPriceChangeRequestsDigestHours,
        divisionalManagerPriceChangeRequestOutcomesDigestHours:
            values.divisionalManagerPriceChangeRequestOutcomesDigestHours,
        storeManagerPriceChangeRequestOutcomesDigestHours:
            values.storeManagerPriceChangeRequestOutcomesDigestHours,
        pricingBatchesModuleEnabled: values.pricingBatchesModuleEnabled,
        priceChangeFreezeStartAt: values.priceChangeFreezeStartAt ?? null,
        priceChangeFreezeEndAt: values.priceChangeFreezeEndAt ?? null,
        priceChangeFreezeBannerMessage:
            values.priceChangeFreezeBannerMessage.trim() || null,
        salesDataMaintenanceStartTime:
            values.salesDataMaintenanceStartTime.trim().slice(0, 5) || null,
        salesDataMaintenanceEndTime:
            values.salesDataMaintenanceEndTime.trim().slice(0, 5) || null,
        letsCreatePriceSyncEnabled: values.letsCreatePriceSyncEnabled,
        trainingModuleEnabled: values.trainingModuleEnabled,
    };
}

export function AppConfigManager() {
    const { data, loading, error } = useQuery(GET_APP_CONFIG, {
        fetchPolicy: 'network-only',
    });
    const { data: divisionsData, loading: divisionsLoading } =
        useQuery(GET_DIVISIONS);
    const [updateConfig, { loading: saving }] = useMutation(UPDATE_APP_CONFIG, {
        refetchQueries: [{ query: GET_APP_CONFIG }],
    });
    const form = useForm<AppConfigFormValues>({
        initialValues,
        validate: {
            vatPercentage: v =>
                v < 0 || v > 100 ? 'Must be between 0 and 100' : null,
            surveyAutoGenDueDays: v =>
                v < 1 || v > 365 ? 'Must be between 1 and 365' : null,
            androidAppVersion: v => (v.trim() === '' ? 'Required' : null),
            iosAppVersion: v => (v.trim() === '' ? 'Required' : null),
            huaweiAppVersion: v => (v.trim() === '' ? 'Required' : null),
            priceChangeFreezeStartAt: value => {
                if (!value) {
                    return null;
                }
                const today = dayjs().tz(SOUTH_AFRICA_TIMEZONE).startOf('day');
                const startDay = dayjs(value)
                    .tz(SOUTH_AFRICA_TIMEZONE)
                    .startOf('day');
                if (startDay.isBefore(today)) {
                    return 'Start date cannot be before today';
                }
                return null;
            },
            priceChangeFreezeEndAt: (value, values) => {
                if (!value || !values.priceChangeFreezeStartAt) {
                    return null;
                }
                if (
                    !isPriceChangeFreezeEndAfterStart(
                        values.priceChangeFreezeStartAt,
                        value,
                    )
                ) {
                    return 'End date must be at least one day after start date';
                }
                return null;
            },
            salesDataMaintenanceStartTime: (value, values) =>
                salesMaintenanceTimeError(
                    value,
                    values.salesDataMaintenanceEndTime,
                ),
            salesDataMaintenanceEndTime: (value, values) =>
                salesMaintenanceTimeError(
                    values.salesDataMaintenanceStartTime,
                    value,
                ),
        },
    });

    useEffect(() => {
        if (data?.appConfig) {
            const config = data.appConfig;
            form.setValues({
                maintenanceMode: config.maintenanceMode,
                maintenanceMessage: config.maintenanceMessage ?? '',
                surveyAutoGenEnabled: config.surveyAutoGenEnabled ?? true,
                surveyAutoGenDueDays: config.surveyAutoGenDueDays ?? 14,
                vatPercentage: config.vatPercentage,
                divisionManagerApprovalSlaMinutes:
                    config.divisionManagerApprovalSlaMinutes,
                opsExecApprovalSlaMinutes: config.opsExecApprovalSlaMinutes,
                seniorOpsExecApprovalSlaMinutes:
                    config.seniorOpsExecApprovalSlaMinutes,
                procurementManagerApprovalSlaMinutes:
                    config.procurementManagerApprovalSlaMinutes,
                procurementExecApprovalSlaMinutes:
                    config.procurementExecApprovalSlaMinutes,
                procurementSupplierEngagementSlaMinutes:
                    config.procurementSupplierEngagementSlaMinutes,
                androidAppVersion: config.androidAppVersion,
                iosAppVersion: config.iosAppVersion,
                huaweiAppVersion: config.huaweiAppVersion,
                appUpdateRequired: config.appUpdateRequired,
                appUpdateMessage: config.appUpdateMessage,
                excludedCountryCodes: config.excludedCountryCodes,
                excludedDivisionSapIds: config.excludedDivisionSapIds ?? '',
                dailyIncompleteSalesReportRecipients:
                    config.dailyIncompleteSalesReportRecipients,
                dmOpsDeclineReasons: config.dmOpsDeclineReasons,
                procurementDeclineReasons: config.procurementDeclineReasons,
                supplierDeclineReasons: config.supplierDeclineReasons,
                interimPriceReasons: config.interimPriceReasons,
                salesReviewEnabled: config.salesReviewEnabled,
                manualSalesAutoDataMaxAgeHours:
                    config.manualSalesAutoDataMaxAgeHours ?? 2,
                pendingPriceChangeRequestsDigestHours:
                    config.pendingPriceChangeRequestsDigestHours ??
                    DEFAULT_PENDING_PRICE_CHANGE_REQUEST_DIGEST_HOURS,
                divisionalManagerPendingPriceChangeRequestsDigestHours:
                    config.divisionalManagerPendingPriceChangeRequestsDigestHours ??
                    DEFAULT_DM_SM_DIGEST_HOURS,
                divisionalManagerPriceChangeRequestOutcomesDigestHours:
                    config.divisionalManagerPriceChangeRequestOutcomesDigestHours ??
                    DEFAULT_DM_SM_DIGEST_HOURS,
                storeManagerPriceChangeRequestOutcomesDigestHours:
                    config.storeManagerPriceChangeRequestOutcomesDigestHours ??
                    DEFAULT_DM_SM_DIGEST_HOURS,
                pricingBatchesModuleEnabled:
                    config.pricingBatchesModuleEnabled ?? false,
                priceChangeFreezeStartAt: config.priceChangeFreezeStartAt
                    ? new Date(config.priceChangeFreezeStartAt)
                    : null,
                priceChangeFreezeEndAt: config.priceChangeFreezeEndAt
                    ? new Date(config.priceChangeFreezeEndAt)
                    : null,
                priceChangeFreezeBannerMessage:
                    config.priceChangeFreezeBannerMessage ?? '',
                salesDataMaintenanceStartTime:
                    config.salesDataMaintenanceStartTime?.slice(0, 5) ?? '',
                salesDataMaintenanceEndTime:
                    config.salesDataMaintenanceEndTime?.slice(0, 5) ?? '',
                letsCreatePriceSyncEnabled:
                    config.letsCreatePriceSyncEnabled ?? false,
                trainingModuleEnabled: config.trainingModuleEnabled ?? false,
            });
            form.resetDirty();
        }
    }, [data]);

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error loading config">
                {error.message}
            </Alert>
        );
    }

    const handleSubmit = async (values: AppConfigFormValues) => {
        try {
            await updateConfig({
                variables: {
                    input: buildUpdateAppConfigInput(values),
                },
            });
            form.resetDirty();
            notifications.show({
                title: 'Config saved',
                message: 'Application configuration has been updated.',
                color: 'green',
            });
        } catch (err: any) {
            notifications.show({
                title: 'Error saving config',
                message: err.message,
                color: 'red',
            });
        }
    };

    const handlePendingPriceChangeDigestHoursChange = (values: string[]) => {
        form.setFieldValue(
            'pendingPriceChangeRequestsDigestHours',
            normalisePendingPriceChangeDigestHours(values),
        );
    };

    const handleDmPendingDigestHoursChange = (values: string[]) => {
        form.setFieldValue(
            'divisionalManagerPendingPriceChangeRequestsDigestHours',
            normalisePendingPriceChangeDigestHours(values),
        );
    };

    const handleDmOutcomesDigestHoursChange = (values: string[]) => {
        form.setFieldValue(
            'divisionalManagerPriceChangeRequestOutcomesDigestHours',
            normalisePendingPriceChangeDigestHours(values),
        );
    };

    const handleSmOutcomesDigestHoursChange = (values: string[]) => {
        form.setFieldValue(
            'storeManagerPriceChangeRequestOutcomesDigestHours',
            normalisePendingPriceChangeDigestHours(values),
        );
    };

    const handlePriceChangeFreezeStartChange = (
        value: Date | string | null,
    ) => {
        const startAt = value ? new Date(value) : null;
        form.setFieldValue('priceChangeFreezeStartAt', startAt);

        const endAt = form.values.priceChangeFreezeEndAt;
        if (
            startAt &&
            endAt &&
            !isPriceChangeFreezeEndAfterStart(startAt, endAt)
        ) {
            form.setFieldValue('priceChangeFreezeEndAt', null);
        }
    };

    const divisionOptions = (divisionsData?.getDivisions ?? [])
        .filter(division => !!division.sapId)
        .map(division => ({
            value: division.sapId,
            label: `${division.sapId} - ${division.name}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <MaintenanceTabPanelHeader>
                <div className="flex items-center w-full">
                    <div className="flex items-center gap-2">
                        <IconSettings size={24} />
                        <span className="text-lg font-medium">
                            Application Configuration
                        </span>
                    </div>
                    <div className="ml-auto flex items-center">
                        <Button
                            type="submit"
                            size="sm"
                            loading={saving}
                            disabled={!form.isDirty()}
                        >
                            Save Configuration
                        </Button>
                    </div>
                </div>
                <Text size="sm" c="dimmed" mt="xs">
                    Manage application settings. Changes take effect immediately
                    (maintenance mode) or on next API request (other values).
                </Text>
            </MaintenanceTabPanelHeader>

            {/* System / Maintenance */}
            <SectionCard title="System">
                <Stack gap="md">
                    <div className="flex items-center gap-4">
                        <Switch
                            label="Maintenance Mode"
                            description="When enabled, the application is inaccessible to all users"
                            color="red"
                            size="md"
                            {...form.getInputProps('maintenanceMode', {
                                type: 'checkbox',
                            })}
                        />
                        {form.values.maintenanceMode && (
                            <Alert
                                color="orange"
                                icon={<IconAlertTriangle size={16} />}
                                className="flex-1"
                                p="xs"
                            >
                                The application will be inaccessible to all
                                users while maintenance mode is active.
                            </Alert>
                        )}
                    </div>

                    <Textarea
                        label="Maintenance Message"
                        description="Shown to users when in maintenance mode or when the API is unreachable"
                        autosize
                        minRows={2}
                        size="md"
                        {...form.getInputProps('maintenanceMessage')}
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Surveys">
                <Stack gap="md">
                    <Switch
                        label="Survey Auto-Generation"
                        description="When enabled, bi-weekly price surveys are created automatically (every second financial week on Monday)"
                        size="md"
                        {...form.getInputProps('surveyAutoGenEnabled', {
                            type: 'checkbox',
                        })}
                    />
                    <NumberInput
                        label="Survey Due Days"
                        description="Number of days from creation until auto-generated surveys are due (default: 14)"
                        min={1}
                        max={365}
                        {...form.getInputProps('surveyAutoGenDueDays')}
                    />
                </Stack>
            </SectionCard>

            {/* Business Config */}
            <SectionCard title="Business">
                <Stack gap="md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <NumberInput
                            label="VAT %"
                            description="Global fallback VAT rate, used when a store's country has no configured rate below"
                            min={0}
                            max={100}
                            decimalScale={2}
                            {...form.getInputProps('vatPercentage')}
                        />
                        <TagsInput
                            label="Excluded Country Codes"
                            description="Add country codes (e.g. MW, ZW)"
                            placeholder="Add country code"
                            splitChars={[',', ' ', ';']}
                            value={commaSeparatedToArray(
                                form.values.excludedCountryCodes,
                            )}
                            onChange={values =>
                                form.setFieldValue(
                                    'excludedCountryCodes',
                                    arrayToCommaSeparated(values),
                                )
                            }
                            radius="lg"
                            size="md"
                        />
                    </div>

                    <MultiSelect
                        label="Excluded Divisions"
                        description="Sales, reports, notifications and crons will ignore stores belonging to these divisions"
                        placeholder={
                            divisionsLoading
                                ? 'Loading divisions...'
                                : 'Select divisions to exclude'
                        }
                        data={divisionOptions}
                        value={commaSeparatedToArray(
                            form.values.excludedDivisionSapIds,
                        )}
                        onChange={values =>
                            form.setFieldValue(
                                'excludedDivisionSapIds',
                                arrayToCommaSeparated(values),
                            )
                        }
                        searchable
                        clearable
                        radius="lg"
                        size="md"
                        disabled={divisionsLoading}
                    />

                    <TagsInput
                        label="Daily Incomplete Sales Report Recipients"
                        description="Add email addresses"
                        placeholder="Add email"
                        splitChars={[',', ' ', ';']}
                        value={commaSeparatedToArray(
                            form.values.dailyIncompleteSalesReportRecipients,
                        )}
                        onChange={values =>
                            form.setFieldValue(
                                'dailyIncompleteSalesReportRecipients',
                                arrayToCommaSeparated(values),
                            )
                        }
                        radius="lg"
                        size="md"
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Country VAT Rates">
                <CountryVatConfigManager />
            </SectionCard>

            <SectionCard title="SAP Tax Codes">
                <TaxCodeConfigManager />
            </SectionCard>

            <SectionCard title="Decline Reasons">
                <Stack gap="md">
                    <Text size="sm" c="dimmed" mb="xs">
                        Manage the decline reason options available to each
                        role. Type a reason and press Enter or click + to add
                        it.
                    </Text>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ReasonListEditor
                            label="Ops Decline Reasons"
                            description="Reasons available to Division Managers and Ops Executives"
                            value={commaSeparatedToArray(
                                form.values.dmOpsDeclineReasons,
                            )}
                            onChange={values =>
                                form.setFieldValue(
                                    'dmOpsDeclineReasons',
                                    arrayToCommaSeparated(values),
                                )
                            }
                        />
                        <ReasonListEditor
                            label="Procurement Decline Reasons"
                            description="Reasons available to Procurement Managers and Executives"
                            value={commaSeparatedToArray(
                                form.values.procurementDeclineReasons,
                            )}
                            onChange={values =>
                                form.setFieldValue(
                                    'procurementDeclineReasons',
                                    arrayToCommaSeparated(values),
                                )
                            }
                        />
                        <ReasonListEditor
                            label="Supplier Decline Reasons"
                            description="Reasons available for supplier decline feedback"
                            value={commaSeparatedToArray(
                                form.values.supplierDeclineReasons,
                            )}
                            onChange={values =>
                                form.setFieldValue(
                                    'supplierDeclineReasons',
                                    arrayToCommaSeparated(values),
                                )
                            }
                        />
                        <ReasonListEditor
                            label="Interim Price Reasons"
                            description="Reasons available when suggesting an interim price"
                            value={commaSeparatedToArray(
                                form.values.interimPriceReasons,
                            )}
                            onChange={values =>
                                form.setFieldValue(
                                    'interimPriceReasons',
                                    arrayToCommaSeparated(values),
                                )
                            }
                        />
                    </div>
                </Stack>
            </SectionCard>

            {/* Daily Sales Review */}
            <SectionCard title="Daily Sales Review">
                <Stack gap="md">
                    <Switch
                        label="Sales Review Tracking Enabled"
                        description="When enabled, the app tracks whether managers review daily sales data and sends compliance emails"
                        size="md"
                        {...form.getInputProps('salesReviewEnabled', {
                            type: 'checkbox',
                        })}
                    />
                    <NumberInput
                        label="Manual capture trigger (hours)"
                        description="Show the red 'capture sales' banner to store managers when no automatic sales data has been received for this many hours. Default: 2."
                        min={1}
                        max={72}
                        {...form.getInputProps(
                            'manualSalesAutoDataMaxAgeHours',
                        )}
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Price change">
                <Stack gap="md">
                    <Switch
                        label="Pricing batches module"
                        description="When enabled, eligible users can open Pricing batches under Price Change and batch APIs are available."
                        size="md"
                        {...form.getInputProps('pricingBatchesModuleEnabled', {
                            type: 'checkbox',
                        })}
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Training & Help">
                <Stack gap="md">
                    <Switch
                        label="Training module"
                        description="When enabled, users can access training materials and help content."
                        size="md"
                        {...form.getInputProps('trainingModuleEnabled', {
                            type: 'checkbox',
                        })}
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="PM pending price change digest">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Hours of the day (South Africa, Africa/Johannesburg) at
                        which procurement managers receive a summary email and
                        push for open price change requests awaiting their
                        approval. Enter hours as whole numbers 0-23 (e.g. 7, 10,
                        13, 16).
                    </Text>
                    <TagsInput
                        label="Digest hours"
                        description="Hours 0-23; the digest runs at the top of each listed hour"
                        placeholder="Add hour (0-23)"
                        splitChars={[',', ' ', ';']}
                        value={commaSeparatedToArray(
                            form.values.pendingPriceChangeRequestsDigestHours,
                        )}
                        onChange={handlePendingPriceChangeDigestHoursChange}
                        radius="lg"
                        size="md"
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="DM pending approval digest">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Hours of the day (Africa/Johannesburg) at which
                        divisional managers receive a summary email and push for
                        open price change requests awaiting their approval. The
                        first configured hour of the day also sweeps overnight
                        activity since the previous day&apos;s last digest hour.
                    </Text>
                    <TagsInput
                        label="Digest hours"
                        description="Hours 0-23; the digest runs at the top of each listed hour"
                        placeholder="Add hour (0-23)"
                        splitChars={[',', ' ', ';']}
                        value={commaSeparatedToArray(
                            form.values
                                .divisionalManagerPendingPriceChangeRequestsDigestHours,
                        )}
                        onChange={handleDmPendingDigestHoursChange}
                        radius="lg"
                        size="md"
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="DM price change outcome digest">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Hours of the day (Africa/Johannesburg) at which
                        divisional managers receive a summary of recent outcomes
                        on price change requests they previously approved
                        (Procurement Approved / Declined / Temp Approved, and
                        zone-level Ops outcomes). Each digest only covers
                        outcomes since the previous configured hour.
                    </Text>
                    <TagsInput
                        label="Digest hours"
                        description="Hours 0-23; the digest runs at the top of each listed hour"
                        placeholder="Add hour (0-23)"
                        splitChars={[',', ' ', ';']}
                        value={commaSeparatedToArray(
                            form.values
                                .divisionalManagerPriceChangeRequestOutcomesDigestHours,
                        )}
                        onChange={handleDmOutcomesDigestHoursChange}
                        radius="lg"
                        size="md"
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="SM price change outcome digest">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Hours of the day (Africa/Johannesburg) at which store
                        managers receive a summary of recent outcomes on their
                        store&apos;s price change requests (Ops Approved /
                        Declined, Procurement Approved / Declined / Temp
                        Approved). Each digest only covers outcomes since the
                        previous configured hour.
                    </Text>
                    <TagsInput
                        label="Digest hours"
                        description="Hours 0-23; the digest runs at the top of each listed hour"
                        placeholder="Add hour (0-23)"
                        splitChars={[',', ' ', ';']}
                        value={commaSeparatedToArray(
                            form.values
                                .storeManagerPriceChangeRequestOutcomesDigestHours,
                        )}
                        onChange={handleSmOutcomesDigestHoursChange}
                        radius="lg"
                        size="md"
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Price change freeze">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        While start and end are both set and the current time
                        falls within the window (inclusive,
                        Africa/Johannesburg), price change requests are sent to
                        the operations executive for approval before
                        procurement.
                    </Text>
                    {data?.appConfig?.priceChangeFreezeIsActive ? (
                        <Alert color="blue" title="Freeze is active now">
                            The configured window includes the current time
                            (Africa/Johannesburg).
                        </Alert>
                    ) : null}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DateTimePicker
                            label="Freeze start"
                            placeholder="Pick date and time"
                            clearable
                            minDate={getPriceChangeFreezeMinStartDate()}
                            value={form.values.priceChangeFreezeStartAt}
                            onChange={handlePriceChangeFreezeStartChange}
                            error={form.errors.priceChangeFreezeStartAt}
                        />
                        <DateTimePicker
                            label="Freeze end"
                            placeholder="Pick date and time"
                            defaultTimeValue="23:59"
                            clearable
                            minDate={getPriceChangeFreezeMinEndDate(
                                form.values.priceChangeFreezeStartAt,
                            )}
                            value={getPriceChangeFreezeEndAtPickerValue(
                                form.values.priceChangeFreezeEndAt,
                            )}
                            onChange={value =>
                                form.setFieldValue(
                                    'priceChangeFreezeEndAt',
                                    normalisePriceChangeFreezeEndAtForPicker(
                                        value,
                                    ),
                                )
                            }
                            error={form.errors.priceChangeFreezeEndAt}
                        />
                    </div>
                    <Textarea
                        label="Banner message (optional)"
                        description="Shown on the price change requests page and mobile action list while a freeze is active"
                        autosize
                        minRows={2}
                        size="md"
                        {...form.getInputProps(
                            'priceChangeFreezeBannerMessage',
                        )}
                    />
                </Stack>
            </SectionCard>

            <SectionCard title="Sales data maintenance">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Daily window in Africa/Johannesburg. While the current
                        time is inside it, product sales return the stored rows
                        without waiting on SAP. A complete range still shows. A
                        missing range shows the maintenance message on price
                        change requests and surveys. The historical-sales feed
                        pauses on its own from 01:00 to 04:00. A window can
                        cross midnight, for example 22:00 to 05:00. Clear both
                        times to turn it off.
                    </Text>
                    {isSalesDataMaintenanceActive(
                        form.values.salesDataMaintenanceStartTime,
                        form.values.salesDataMaintenanceEndTime,
                    ) ? (
                        <Alert
                            color="orange"
                            title="Sales maintenance is active now"
                        >
                            A missing sales range shows the maintenance message
                            until{' '}
                            {form.values.salesDataMaintenanceEndTime.slice(
                                0,
                                5,
                            )}
                            .
                        </Alert>
                    ) : null}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TimeInput
                            label="Start"
                            {...form.getInputProps(
                                'salesDataMaintenanceStartTime',
                            )}
                        />
                        <TimeInput
                            label="End"
                            {...form.getInputProps(
                                'salesDataMaintenanceEndTime',
                            )}
                        />
                    </div>
                </Stack>
            </SectionCard>

            {/* SLA Timers */}
            <SectionCard title="SLA Timers">
                <Stack gap="md">
                    <Text size="sm" c="dimmed" mb="xs">
                        Maximum time allowed for each approval step (in
                        minutes). 2880 = 2 days, 4320 = 3 days.
                    </Text>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <NumberInput
                            label="Division Manager Approval"
                            min={0}
                            {...form.getInputProps(
                                'divisionManagerApprovalSlaMinutes',
                            )}
                        />
                        <NumberInput
                            label="Ops Executive Approval"
                            min={0}
                            {...form.getInputProps('opsExecApprovalSlaMinutes')}
                        />
                        <NumberInput
                            label="Senior Ops Executive Approval"
                            min={0}
                            {...form.getInputProps(
                                'seniorOpsExecApprovalSlaMinutes',
                            )}
                        />
                        <NumberInput
                            label="Procurement Manager Approval"
                            min={0}
                            {...form.getInputProps(
                                'procurementManagerApprovalSlaMinutes',
                            )}
                        />
                        <NumberInput
                            label="Procurement Executive Approval"
                            min={0}
                            {...form.getInputProps(
                                'procurementExecApprovalSlaMinutes',
                            )}
                        />
                        <NumberInput
                            label="Supplier Engagement"
                            min={0}
                            {...form.getInputProps(
                                'procurementSupplierEngagementSlaMinutes',
                            )}
                        />
                    </div>
                </Stack>
            </SectionCard>

            <SectionCard title="LetsCreate price sync">
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        When enabled, future-dated product prices are dispatched
                        to LetsCreate every hour (Africa/Johannesburg). The base
                        URL is configured via the{' '}
                        <Text component="span" fw={600} size="sm">
                            LETSCREATE_BASE_URL
                        </Text>{' '}
                        environment variable. Set the endpoint paths below.
                    </Text>
                    <Input.Wrapper
                        label="Enable LetsCreate price sync"
                        description="When disabled, no future-dated product prices will be dispatched"
                        size="md"
                    >
                        <div className="min-h-[42px] flex items-center">
                            <Switch
                                size="md"
                                {...form.getInputProps(
                                    'letsCreatePriceSyncEnabled',
                                    {
                                        type: 'checkbox',
                                    },
                                )}
                            />
                        </div>
                    </Input.Wrapper>
                </Stack>
            </SectionCard>

            {/* Mobile App */}
            <SectionCard title="Mobile App">
                <Stack gap="md">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <TextInput
                            label="Android Version"
                            description="Minimum required version (e.g. 1.4.2)"
                            {...form.getInputProps('androidAppVersion')}
                        />
                        <TextInput
                            label="iOS Version"
                            description="Minimum required version (e.g. 1.4.2)"
                            {...form.getInputProps('iosAppVersion')}
                        />
                        <TextInput
                            label="Huawei Version"
                            description="Minimum required version (e.g. 1.4.2)"
                            {...form.getInputProps('huaweiAppVersion')}
                        />
                    </div>
                    <Switch
                        label="App Update Required"
                        description="When enabled, users must update to the minimum version before using the app"
                        size="md"
                        {...form.getInputProps('appUpdateRequired', {
                            type: 'checkbox',
                        })}
                    />
                    <Textarea
                        label="App Update Message"
                        description="Shown when users need to update the app"
                        autosize
                        minRows={2}
                        size="md"
                        {...form.getInputProps('appUpdateMessage')}
                    />
                </Stack>
            </SectionCard>
        </form>
    );
}
